import { FetchStrategy } from "@betomorrow/micro-stores"
import { word } from "@modules/core/utils/i18n"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramBundlePriceCard } from "@modules/programBundle/components/programBundlePriceCard"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ItemProgram } from "@modules/programs/programTypes"
import { HUText } from "@modules/ui/components/huText"
import { useTheme } from "styled-components"

type ProgramBundleInfoDetailsProps = {
  programBundle: ProgramBundleEntity
}

export const ProgramBundleInfoDetails: React.FC<ProgramBundleInfoDetailsProps> = ({ programBundle }) => {
  const theme = useTheme()
  const { isAuthor } = useProgramBundleRights(programBundle)
  const { navigateToProgramView } = useProgramSidebar()
  const { programBundleStripeSubscriptionInfos } = useProgramBundleAthleteSubscription(programBundle)
  const subscribedProgramsAsync = usePrograms({ programFilter: "SUBSCRIBED", fetchStrategy: FetchStrategy.Once })

  const programSubscribedInBundle = subscribedProgramsAsync.result.filter(
    (program) => programBundle.programs?.findIndex((p) => p.id === program.id) !== -1,
  )

  const onProgramClick = (program: ItemProgram) => {
    navigateToProgramView(program.id, program._programType, false, {
      leftButtonColor: theme.mainWhite,
      replaceAllComponents: false,
    })
  }

  return (
    <div className="flex flex-column gap-4 py-4 px-2">
      {/* Description */}
      <HUText fontStyle="BM" style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}>
        {programBundle.description}
      </HUText>

      {/* Information about already subscribed in a program of the bundle */}
      {!programBundleStripeSubscriptionInfos && !isAuthor && programSubscribedInBundle.length > 0 && (
        <div className="card banner">
          <HUText fontStyle="BM" color={theme.neutralColor700}>
            {word("program.subscription.doubleSubscription.fromBundle", { count: programSubscribedInBundle.length }) +
              " :"}
          </HUText>

          <div className="flex flex-column mt-2 gap-2 ml-2">
            {programSubscribedInBundle.map((program) => (
              <div
                className="flex flex-row align-items-center gap-2 cursor-pointer"
                key={program.id}
                onClick={() => onProgramClick(program)}
              >
                <ProfileAvatar profile={{ avatar: program.image, username: "" }} size={30} />
                <HUText fontStyle="BM" color={theme.neutralColor700}>
                  {program.title}
                </HUText>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price */}
      <ProgramBundlePriceCard programBundle={programBundle} />

      {/* Owner */}
      <div className="rounded-33 flex flex-row p-4 mb-4 flex-row justify-content-center align-items-center overflow-hidden gap-4 bg-neutralColor-100 border-0">
        <ProfileAvatar
          profile={{ avatar: programBundle.owner.avatar, username: programBundle.owner.firstname || "" }}
          size={90}
        />

        <div className="flex flex-column gap-1 text-center">
          <HUText color={theme.neutralColor700} fontStyle="HS">
            {programBundle.owner.firstname + " " + programBundle.owner.lastname}
          </HUText>

          {programBundle.owner.username && (
            <HUText fontStyle="BM" color={theme.neutralColor900}>
              {"@" + programBundle.owner.username}
            </HUText>
          )}
        </div>
      </div>
    </div>
  )
}
